// ContentHistory.js — View past content generations with copy buttons + editorial calendar
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import ReactMarkdown from "react-markdown";
import {
  ThemeProvider, createTheme, CssBaseline, Box, Typography, Tabs, Tab,
  Card, Collapse, Divider, Chip, Tooltip,
} from "@mui/material";
import { getEmailFromToken } from "./auth";

const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6c7ef8" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text: { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

const D = {
  bg: "#0d0e14",
  surface: "#13151e",
  border: "rgba(255,255,255,0.07)",
  borderMed: "rgba(255,255,255,0.11)",
  accent: "#6c7ef8",
  accentSoft: "rgba(108,126,248,0.1)",
  accentBorder: "rgba(108,126,248,0.2)",
  success: "#34d399",
  successSoft: "rgba(52,211,153,0.1)",
  danger: "#f87171",
  purple: "#a37bf8",
  textPrimary: "#f0f2f7",
  textSec: "#b0b7ce",
  textMuted: "#6b7390",
  fontDisplay: "'Plus Jakarta Sans','DM Sans',sans-serif",
  fontBody: "'DM Sans',sans-serif",
  fontMono: "'JetBrains Mono',monospace",
};

// Platform metadata for calendar display
const CAL_PLATFORM = {
  linkedin:  { label: "LinkedIn",  icon: "💼", color: "#0A66C2", bg: "rgba(10,102,194,0.12)",  border: "rgba(10,102,194,0.28)" },
  instagram: { label: "Instagram", icon: "📸", color: "#E1306C", bg: "rgba(225,48,108,0.12)",  border: "rgba(225,48,108,0.28)" },
  facebook:  { label: "Facebook",  icon: "📘", color: "#1877F2", bg: "rgba(24,119,242,0.12)",  border: "rgba(24,119,242,0.28)" },
};

const CONTENT_TYPE_COLORS = {
  "Educational":       { bg: "rgba(108,126,248,0.14)", color: "#818cf8" },
  "Thought Leadership":{ bg: "rgba(163,123,248,0.14)", color: "#a37bf8" },
  "Engagement":        { bg: "rgba(52,211,153,0.12)",  color: "#34d399" },
  "Story":             { bg: "rgba(245,158,11,0.12)",  color: "#f59e0b" },
  "Inspirational":     { bg: "rgba(225,48,108,0.11)",  color: "#f472b6" },
  "Promotional":       { bg: "rgba(59,130,246,0.12)",  color: "#60a5fa" },
  "Trending":          { bg: "rgba(29,155,240,0.12)",  color: "#38bdf8" },
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background: #0d0e14; color: #f0f2f7; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
  ::selection { background: rgba(108,126,248,0.25); }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
  @keyframes spin    { to { transform:rotate(360deg); } }
  @keyframes logoGlow { 0%,100%{box-shadow:0 0 16px rgba(108,126,248,0.3)} 50%{box-shadow:0 0 28px rgba(163,123,248,0.45)} }
  @keyframes shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes calCardIn { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
  .fade-up { animation: fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }
  .content-post { background:rgba(255,255,255,0.025); border:1px solid rgba(255,255,255,0.07); border-radius:10px; padding:16px 18px; margin-bottom:10px; transition:border-color 0.18s; position:relative; }
  .content-post:hover { border-color:rgba(255,255,255,0.13); }
  .post-index { position:absolute; top:12px; right:48px; font-family:'JetBrains Mono',monospace; font-size:10.5px; color:rgba(255,255,255,0.22); font-weight:500; }
  .copy-btn { position:absolute; top:10px; right:12px; background:rgba(108,126,248,0.08); border:1px solid rgba(108,126,248,0.18); border-radius:6px; padding:3px 9px; font-size:11px; font-family:'JetBrains Mono',monospace; color:#8b97c8; cursor:pointer; transition:all 0.15s; }
  .copy-btn:hover { background:rgba(108,126,248,0.16); color:#a8b4fc; border-color:rgba(108,126,248,0.35); }
  .copy-btn.copied { color:#34d399; border-color:rgba(52,211,153,0.35); background:rgba(52,211,153,0.08); }
  .topic-card { background:#13151e; border:1px solid rgba(255,255,255,0.07); border-radius:14px; margin-bottom:14px; overflow:hidden; transition:border-color 0.2s; }
  .topic-card:hover { border-color:rgba(255,255,255,0.13); }
  .topic-head { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; cursor:pointer; transition:background 0.15s; flex-wrap:wrap; gap:8px; }
  .topic-head:hover { background:rgba(255,255,255,0.02); }
  .chevron { transition:transform 0.25s; display:inline-block; }
  .chevron.open { transform:rotate(180deg); }
  .MuiTab-root { text-transform:none !important; font-family:'DM Sans',sans-serif !important; font-weight:500 !important; font-size:13px !important; min-height:42px !important; color:#b0b7ce !important; }
  .MuiTab-root.Mui-selected { color:#f0f2f7 !important; font-weight:600 !important; }
  .MuiTabs-indicator { background:#6c7ef8 !important; height:2px !important; border-radius:2px !important; }
  .citation-row { display:flex; align-items:flex-start; gap:10px; padding:10px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .citation-row:last-child { border-bottom:none; }
  .citation-num { font-family:'JetBrains Mono',monospace; font-size:10.5px; color:rgba(108,126,248,0.7); font-weight:600; flex-shrink:0; padding-top:2px; }
  .sk { background:rgba(255,255,255,0.05); background-image:linear-gradient(90deg,rgba(255,255,255,0.04) 0px,rgba(255,255,255,0.08) 300px,rgba(255,255,255,0.04) 600px); background-size:600px; animation:shimmer 1.6s ease infinite; border-radius:8px; }

  /* ── Calendar styles ── */
  .cal-week-pill {
    display:inline-flex; align-items:center; justify-content:center;
    padding:6px 16px; border-radius:100px; font-size:12.5px; font-weight:600;
    font-family:'DM Sans',sans-serif; cursor:pointer; border:1px solid transparent;
    transition:all 0.2s; user-select:none;
    background:rgba(255,255,255,0.04); border-color:rgba(255,255,255,0.08); color:#8b91a8;
  }
  .cal-week-pill:hover { background:rgba(255,255,255,0.07); color:#c8cede; }
  .cal-week-pill.active { background:rgba(108,126,248,0.15); border-color:rgba(108,126,248,0.35); color:#f0f2f7; }

  .cal-slot-card {
    background:#1a1b24; border:1px solid rgba(255,255,255,0.08); border-radius:13px;
    padding:17px; position:relative; overflow:hidden;
    transition:border-color 0.25s, transform 0.25s, box-shadow 0.25s;
    animation: calCardIn 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  .cal-slot-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    border-radius:13px 13px 0 0;
    opacity:0; transition:opacity 0.25s;
  }
  .cal-slot-card:hover {
    transform:translateY(-3px);
    box-shadow:0 12px 36px rgba(0,0,0,0.35), 0 0 24px rgba(108,126,248,0.06);
  }
  .cal-slot-card:hover::before { opacity:1; }

  .cal-schedule-btn {
    display:inline-flex; align-items:center; justify-content:center; gap:8px;
    padding:11px 22px; border-radius:9px; border:none; cursor:pointer;
    font-family:'DM Sans',sans-serif; font-size:13.5px; font-weight:700;
    background:linear-gradient(135deg,#6c7ef8,#a37bf8); color:white;
    transition:all 0.22s cubic-bezier(0.34,1.56,0.64,1);
  }
  .cal-schedule-btn:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(108,126,248,0.4); }
  .cal-schedule-btn:active { transform:scale(0.96); }

  .cal-ghost-btn {
    display:inline-flex; align-items:center; gap:7px;
    padding:11px 18px; border-radius:9px; cursor:pointer;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); color:#b0b7ce;
    transition:all 0.2s;
  }
  .cal-ghost-btn:hover { background:rgba(255,255,255,0.07); border-color:rgba(255,255,255,0.16); color:#f0f2f7; }

  @media (max-width: 768px) {
    .topic-head { padding: 14px 16px; }
    .topic-card { border-radius: 12px; }
    .content-post { padding: 14px 14px; }
    .copy-btn { position: static; display: block; margin-top: 10px; width: fit-content; }
    .post-index { position: static; display: inline-block; margin-bottom: 6px; right: auto; top: auto; }
    .MuiTab-root { font-size: 11px !important; min-height: 38px !important; padding: 0 8px !important; }
  }
  @media (max-width: 480px) {
    .topic-head { padding: 12px 14px; }
  }
`;

const PLATFORMS = [
  { label: "Facebook",   icon: "📘", key: "facebook"    },
  { label: "Instagram",  icon: "📸", key: "instagram"   },
  { label: "LinkedIn",   icon: "💼", key: "linkedin"    },
  { label: "Twitter",    icon: "𝕏",  key: "twitter"     },
  { label: "Blogs",      icon: "📝", key: "blogs"       },
  { label: "Newsletter", icon: "📬", key: "newsletters" },
  { label: "Citations",  icon: "🔗", key: "citations"   },
];

function normaliseCitation(c) {
  if (typeof c === "string") return { title: c, url: "", source: "", key_finding: "" };
  if (!c || typeof c !== "object") return null;
  const pick = (...keys) => { for (const k of keys) { const v = c[k]; if (v && typeof v === "string" && v.trim()) return v.trim(); } return ""; };
  return {
    title:       pick("title","TITLE","name","NAME"),
    url:         pick("url","URL","link","LINK"),
    source:      pick("source","SOURCE","AUTHOR/OUTLET","author"),
    key_finding: pick("key_finding","KEY FINDING","ONE-LINE HOOK","finding"),
  };
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(typeof text === "object" ? JSON.stringify(text, null, 2) : String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }, [text]);
  return (
    <button className={`copy-btn${copied ? " copied" : ""}`} onClick={handleCopy}>
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function PlatformTabPanel({ children, value, index }) {
  return <>{value === index && <Box sx={{ mt: 2 }}>{children}</Box>}</>;
}

function CitationsPanel({ citations = [] }) {
  if (!citations?.length) return <Typography sx={{ fontSize: 13.5, color: D.textSec, mt: 2 }}>No citations.</Typography>;
  const normalised = citations.map(normaliseCitation).filter(Boolean);
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 2 }}>
        Sources · {normalised.length}
      </Typography>
      {normalised.map((c, i) => (
        <div key={i} className="citation-row">
          <span className="citation-num">[{i + 1}]</span>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {c.title && <Typography sx={{ fontSize: 13, fontWeight: 600, color: D.textPrimary, mb: 0.4 }}>{c.title}</Typography>}
            {c.source && <Typography sx={{ fontSize: "11.5px", color: D.textMuted, fontFamily: D.fontMono, mb: 0.4 }}>{c.source}</Typography>}
            {c.key_finding && <Typography sx={{ fontSize: 12.5, color: D.textSec, mb: 0.5, fontStyle: "italic" }}>{c.key_finding}</Typography>}
            {c.url && (
              <Typography component="a" href={c.url} target="_blank" rel="noopener noreferrer"
                sx={{ fontSize: "11.5px", fontFamily: D.fontMono, color: D.accent, textDecoration: "none", wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}>
                {c.url}
              </Typography>
            )}
          </Box>
        </div>
      ))}
    </Box>
  );
}

// ─── Editorial Calendar Tab ───────────────────────────────────────────────────
function CalendarTabContent({ taskId, topic }) {
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [activeWeek, setActiveWeek] = useState(1);
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    api.get(`/social/calendar/${taskId}`)
      .then(r => { setData(r.data); })
      .catch(e => setError(e.response?.data?.detail || "Could not load calendar"))
      .finally(() => setLoading(false));
  }, [taskId]);

  const _TZ_ABBR = { CDT:"CT",CST:"CT",EDT:"ET",EST:"ET",PDT:"PT",PST:"PT",MDT:"MT",MST:"MT",AKDT:"AKT",AKST:"AKT",HST:"HT",HDT:"HT",AST:"AT",ADT:"AT" };

  function _validTz(tz) {
    try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return tz; }
    catch { return "America/Chicago"; }
  }

  function tzAbbr(ianaTimezone) {
    try {
      const raw = new Intl.DateTimeFormat("en-US", { timeZone: ianaTimezone, timeZoneName: "short" })
        .formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value || "CT";
      return _TZ_ABBR[raw] || raw;
    } catch { return "CT"; }
  }

  function formatSlotDate(slot) {
    const tz = _validTz(slot.timezone || "America/Chicago");
    const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const now = new Date();
    const todayIdx = now.getDay();
    const targetIdx = dayNames.indexOf(slot.day_of_week);
    const weekOffset = (slot.week_number - 1) * 7;
    const daysUntil = (targetIdx - todayIdx + 7) % 7 || 7;
    const target = new Date(now);
    target.setDate(now.getDate() + weekOffset + daysUntil);
    try {
      const dateStr = target.toLocaleDateString("en-CA", { timeZone: tz });
      const label = new Date(`${dateStr}T${slot.time}:00`).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz,
      });
      return `${label} ${tzAbbr(tz)}`;
    } catch {
      return `${slot.day_of_week} · ${slot.time} CT`;
    }
  }

  function handleScheduleAll() {
    if (!data?.calendar) return;
    navigate("/autopost", { state: { calendar: data.calendar, topic: data.topic, imageUrls: data.image_urls || [] } });
  }

  function handleCopyCalendar() {
    if (!data?.calendar) return;
    const text = data.calendar.map(s =>
      `${s.day_of_week} ${s.time} | ${CAL_PLATFORM[s.platform]?.label || s.platform} | ${s.content_type}\n${s.content.slice(0, 100)}...\nWhy: ${s.reasoning}`
    ).join("\n\n");
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {[1,2,3].map(i => <Box key={i} className="sk" sx={{ height: 110, borderRadius: "13px" }} />)}
    </Box>
  );

  if (error) return (
    <Box sx={{ mt: 2, p: 2.5, background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "12px" }}>
      <Typography sx={{ fontSize: 13.5, color: "#f87171" }}>⚠️ {error}</Typography>
    </Box>
  );

  if (!data?.calendar?.length) return (
    <Box sx={{ mt: 2, textAlign: "center", py: 6 }}>
      <Typography sx={{ fontSize: 36, mb: 2, opacity: 0.5 }}>📅</Typography>
      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 17, color: D.textPrimary, mb: 1 }}>No calendar available</Typography>
      <Typography sx={{ fontSize: 13, color: D.textSec }}>This generation doesn't have platform posts to schedule yet.</Typography>
    </Box>
  );

  const weeks = [1, 2, 3, 4].filter(w => data.calendar.some(s => s.week_number === w));
  const weekSlots = data.calendar.filter(s => s.week_number === activeWeek);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 5, height: 20, borderRadius: "3px", background: `linear-gradient(180deg, ${D.accent}, ${D.purple})` }} />
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 15, color: D.textPrimary }}>
              Editorial Calendar
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {Object.keys(CAL_PLATFORM).filter(p => data.calendar.some(s => s.platform === p)).map(p => {
              const m = CAL_PLATFORM[p];
              return (
                <span key={p} style={{ fontSize: 11, background: m.bg, border: `1px solid ${m.border}`, borderRadius: "6px", padding: "2px 8px", color: m.color, fontWeight: 600, fontFamily: D.fontMono }}>
                  {m.icon}
                </span>
              );
            })}
          </Box>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.textMuted }}>
            {data.total} posts · 30 days
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <button className="cal-ghost-btn" onClick={handleCopyCalendar}>
            {copied ? "✓ Copied" : "📋 Copy as text"}
          </button>
          <button className="cal-schedule-btn" onClick={handleScheduleAll}>
            ⚡ Schedule All {data.total} Posts
          </button>
        </Box>
      </Box>

      {/* Strategy explanation strip */}
      <Box sx={{ mb: 3, p: 2, background: "rgba(108,126,248,0.05)", border: "1px solid rgba(108,126,248,0.15)", borderRadius: "11px", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
        <Typography sx={{ fontSize: 12.5, color: D.textSec, lineHeight: 1.7 }}>
          <strong style={{ color: D.textPrimary }}>Why this schedule?</strong> Each post is assigned to its platform's peak engagement window — the time of week when professionals on that platform are most likely to read, save, and share. Spacing is distributed to prevent algorithm suppression from same-platform back-to-back posting.
        </Typography>
      </Box>

      {/* Week selector */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
        {weeks.map(w => (
          <button
            key={w}
            onClick={() => setActiveWeek(w)}
            className={`cal-week-pill${activeWeek === w ? " active" : ""}`}
          >
            Week {w}
          </button>
        ))}
      </Box>

      {/* Slots grid */}
      {weekSlots.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: D.textMuted }}>No slots in this week.</Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
          {weekSlots.map((slot, idx) => {
            const pm = CAL_PLATFORM[slot.platform];
            const ct = CONTENT_TYPE_COLORS[slot.content_type] || { bg: "rgba(108,126,248,0.12)", color: D.accent };
            if (!pm) return null;
            return (
              <div
                key={idx}
                className="cal-slot-card"
                style={{
                  borderColor: pm.border,
                  animationDelay: `${idx * 0.06}s`,
                }}
              >
                {/* Top accent bar */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${pm.color}88, ${pm.color}22)`, borderRadius: "13px 13px 0 0" }} />

                {/* Platform + content type */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <span style={{ fontSize: 15 }}>{pm.icon}</span>
                    <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: pm.color }}>{pm.label}</Typography>
                  </Box>
                  <span style={{ fontSize: 10.5, background: ct.bg, color: ct.color, padding: "2px 8px", borderRadius: "5px", fontWeight: 700, fontFamily: D.fontMono }}>
                    {slot.content_type}
                  </span>
                </Box>

                {/* Day + time */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "6px", px: 1, py: 0.4 }}>
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.textPrimary, fontWeight: 600 }}>
                      {formatSlotDate(slot)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "inline-flex", alignItems: "center", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "6px", px: 1, py: 0.4 }}>
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.accent, fontWeight: 700 }}>
                      {slot.time}
                    </Typography>
                  </Box>
                </Box>

                {/* Post preview */}
                <Typography sx={{ fontSize: 12.5, color: "#c0c7de", lineHeight: 1.65, mb: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {slot.content}
                </Typography>

                {/* WHY reasoning */}
                <Box sx={{ p: 1.25, background: "rgba(255,255,255,0.03)", borderRadius: "7px", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 12, flexShrink: 0, marginTop: "1px" }}>💡</span>
                    <Typography sx={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.6, fontStyle: "italic" }}>
                      {slot.reasoning}
                    </Typography>
                  </Box>
                </Box>

                {/* Schedule this slot button */}
                <button
                  onClick={() => navigate("/autopost", { state: { calendar: [slot], topic: slot.topic, singleSlot: true, imageUrls: data.image_urls || [] } })}
                  style={{
                    marginTop: 12, width: "100%", padding: "8px 0", borderRadius: "7px",
                    background: pm.bg, border: `1px solid ${pm.border}`, color: pm.color,
                    fontSize: 12, fontWeight: 700, fontFamily: D.fontBody, cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={e => e.currentTarget.style.opacity = "0.8"}
                  onMouseOut={e => e.currentTarget.style.opacity = "1"}
                >
                  Schedule this post →
                </button>
              </div>
            );
          })}
        </Box>
      )}

      {/* Platform timing legend */}
      <Box sx={{ mt: 3, p: 2, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "11px" }}>
        <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "1px", mb: 1.5 }}>
          Peak Posting Windows Used · times in your timezone
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1 }}>
          {[
            { platform: "linkedin",  times: "Tue 9 AM, Wed 12 PM, Thu 2 PM" },
            { platform: "instagram", times: "Mon 5 PM, Tue 2 PM, Wed 1 PM" },
            { platform: "facebook",  times: "Tue 1 PM, Thu 4 PM, Sat 11 AM" },
          ].map(({ platform, times }) => {
            const m = CAL_PLATFORM[platform];
            if (!m) return null;
            return (
              <Box key={platform} sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                <span style={{ fontSize: 13, flexShrink: 0 }}>{m.icon}</span>
                <Box>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: m.color, mb: 0.2 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: D.textMuted, fontFamily: D.fontMono }}>{times}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Content Entry (one topic card) ──────────────────────────────────────────
function ContentEntry({ entry, autoPosting, hasMedium, hasContentIntelligence, hasCompliance }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab]   = useState(0);

  const result = entry.result || entry;
  const createdAt = (() => {
    if (!entry.created_at) return null;
    const d = new Date(entry.created_at);
    try {
      return d.toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      });
    } catch { return entry.created_at; }
  })();
  const topicText = entry.topic || result.topic || "Untitled";
  const taskId = entry.task_id;

  const platformCounts = PLATFORMS.map(p => ({
    ...p,
    count: result[p.key]?.length || 0,
  })).filter(p => p.count > 0);

  // CI tabs shown when data is present
  const CI_TABS = [
    ...(result.strategy_brief      ? [{ label: "Strategy",   icon: "📋", key: "strategy_brief",    type: "strategy"   }] : []),
    ...(result.competitor_analysis ? [{ label: "Competitor", icon: "🔍", key: "competitor_analysis",type: "competitor" }] : []),
    ...(hasMedium && result.medium_articles?.length > 0 ? [{ label: "Medium", icon: "✍️", key: "medium_articles", type: "medium" }] : []),
  ];

  const ALL_TABS = [
    ...PLATFORMS,
    ...CI_TABS,
    ...(autoPosting ? [{ label: "Calendar", icon: "📅", key: "calendar" }] : []),
  ];

  return (
    <div className="topic-card fade-up">
      <div className="topic-head" onClick={() => setOpen(o => !o)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: D.success, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 14.5, color: D.textPrimary, minWidth: 0 }}>
            {topicText}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", ml: 1 }}>
            {platformCounts.map(p => (
              <Chip key={p.key} label={`${p.icon} ${p.count}`} size="small"
                sx={{ background: "rgba(255,255,255,0.06) !important", color: `${D.textSec} !important`, height: "20px !important", fontSize: "11px !important" }} />
            ))}
            {autoPosting && (
              <Chip label="📅" size="small"
                sx={{ background: "rgba(108,126,248,0.1) !important", color: `${D.accent} !important`, height: "20px !important", fontSize: "11px !important", border: "1px solid rgba(108,126,248,0.2) !important" }} />
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          {createdAt && (
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted }}>
              {createdAt}
            </Typography>
          )}
          <span className={`chevron${open ? " open" : ""}`} style={{ color: D.textMuted, fontSize: 18 }}>▾</span>
        </Box>
      </div>

      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3.5 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1.5, borderBottom: `1px solid ${D.border}`, mb: 0 }}>
            {ALL_TABS.map((p, i) => (
              <Tab key={i} label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <span>{p.icon}</span><span>{p.label}</span>
                  {i < 7 && result[p.key]?.length > 0 && (
                    <Box sx={{ px: 0.75, py: 0.1, borderRadius: "5px", background: `${D.accent}18`, minWidth: 18, textAlign: "center" }}>
                      <Typography sx={{ fontSize: "10px", color: D.accent, fontFamily: D.fontMono, fontWeight: 600, lineHeight: 1.5 }}>
                        {result[p.key].length}
                      </Typography>
                    </Box>
                  )}
                </Box>
              } />
            ))}
          </Tabs>

          {/* Report banners */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, display: "flex", alignItems: "center", gap: 0.5 }}>
              📋 <span>Quality report included in your ZIP — all auto-corrections documented.</span>
            </Typography>
            {hasCompliance && (
              <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, display: "flex", alignItems: "center", gap: 0.5 }}>
                🛡️ <span>Compliance report included — every post reviewed against industry regulations.</span>
              </Typography>
            )}
          </Box>

          {/* Social platforms (0-3): Facebook, Instagram, LinkedIn, Twitter */}
          {[0, 1, 2, 3].map(ti => {
            const platform = PLATFORMS[ti];
            const posts = result[platform.key] || [];
            const platformHooks = result.hook_variants?.[platform.key] || null;
            const HOOKS = [
              { key: "curiosity", label: "Curiosity Hook", why: "Opens a knowledge gap — reader keeps scrolling to resolve the tension" },
              { key: "authority", label: "Authority Hook",  why: "Leads with a credibility signal — builds trust before the message lands" },
              { key: "fomo",      label: "FOMO Hook",       why: "Creates urgency through loss aversion — drives faster action" },
            ];
            return (
              <PlatformTabPanel key={ti} value={tab} index={ti}>
                {posts.length > 0
                  ? posts.map((post, i) => {
                      const hookSet = platformHooks?.[i] || null;
                      return (
                        <div key={i} className="content-post">
                          <span className="post-index">#{String(i + 1).padStart(2, "0")}</span>
                          <CopyButton text={post} />
                          <Typography sx={{ fontSize: "13.5px", lineHeight: 1.75, pr: 4, whiteSpace: "pre-wrap", color: "#d0d5e8" }}>
                            {post}
                          </Typography>
                          {hookSet && (
                            <Box sx={{ mt: 1.5 }}>
                              {HOOKS.map(({ key, label, why }) => hookSet[key] && (
                                <Box key={key} sx={{ mb: 1, p: 1.5, background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: `1px solid ${D.border}`, position: "relative" }}>
                                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</Typography>
                                  <Typography sx={{ fontSize: "10.5px", color: D.textMuted, mb: 0.5, fontStyle: "italic" }}>{why}</Typography>
                                  <CopyButton text={hookSet[key]} />
                                  <Typography sx={{ fontSize: "13px", color: "#c0c7de", pr: 4 }}>{hookSet[key]}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </div>
                      );
                    })
                  : <Typography sx={{ fontSize: 13.5, color: D.textSec, mt: 2 }}>No {platform.label} content.</Typography>
                }
              </PlatformTabPanel>
            );
          })}

          {/* Blogs (index 4) */}
          <PlatformTabPanel value={tab} index={4}>
            {result.blogs?.length > 0
              ? result.blogs.map((b, i) => {
                  const content = b.content || b;
                  return (
                    <Card key={i} sx={{ mb: 3, p: 3, position: "relative", background: "rgba(255,255,255,0.02) !important", border: `1px solid ${D.border} !important` }}>
                      <Box sx={{ position: "absolute", top: 14, right: 14 }}>
                        <CopyButton text={typeof content === "string" ? content : JSON.stringify(content)} />
                      </Box>
                      <ReactMarkdown components={{
                        h1: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", mb: 2, color: D.textPrimary, pr: 6 }}>{children}</Typography>,
                        h2: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", mt: 3, mb: 1.5, color: D.textPrimary }}>{children}</Typography>,
                        h3: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", mt: 2, mb: 1, color: D.textPrimary }}>{children}</Typography>,
                        p:  ({ children }) => <Typography sx={{ fontSize: "14px", lineHeight: 1.8, mb: 1.5, color: "#c0c7de" }}>{children}</Typography>,
                        strong: ({ children }) => <Box component="span" sx={{ fontWeight: 700, color: D.textPrimary }}>{children}</Box>,
                        blockquote: ({ children }) => <Box sx={{ borderLeft: `3px solid ${D.accent}`, pl: 2, py: 0.5, my: 2, background: `${D.accent}08`, borderRadius: "0 8px 8px 0" }}><Typography sx={{ fontSize: "14px", fontStyle: "italic", color: "#b8c0d8" }}>{children}</Typography></Box>,
                        ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                        ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                        li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}><Typography sx={{ fontSize: "14px", color: "#c0c7de" }}>{children}</Typography></Box>,
                        hr: () => <Divider sx={{ my: 3, borderColor: D.border }} />,
                        code: ({ children }) => <Box component="span" sx={{ fontFamily: D.fontMono, background: `${D.accent}12`, px: 0.75, py: 0.25, borderRadius: "4px", fontSize: "0.85em", color: D.accent }}>{children}</Box>,
                      }}>{typeof content === "string" ? content : String(content)}</ReactMarkdown>
                      {hasContentIntelligence && (b.metaTitle || b.metaDesc || b.primaryKeyword) && (
                        <Box sx={{ mt: 2.5, p: 2, background: `${D.accent}08`, borderRadius: "9px", border: `1px solid ${D.accentBorder}` }}>
                          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", fontWeight: 600, color: D.accent, letterSpacing: "0.07em", mb: 1 }}>SEO METADATA</Typography>
                          {b.metaTitle      && <Typography sx={{ fontSize: 12, color: D.textSec, mb: 0.4 }}><strong>Title:</strong> {b.metaTitle}</Typography>}
                          {b.metaDesc       && <Typography sx={{ fontSize: 12, color: D.textSec, mb: 0.4 }}><strong>Meta Description:</strong> {b.metaDesc}</Typography>}
                          {b.primaryKeyword && <Typography sx={{ fontSize: 12, color: D.textSec, mb: 0.4 }}><strong>Primary Keyword:</strong> {b.primaryKeyword}</Typography>}
                          {b.relatedKw      && <Typography sx={{ fontSize: 12, color: D.textSec, mb: 0.4 }}><strong>Related Keywords:</strong> {b.relatedKw}</Typography>}
                          {b.searchIntent   && <Typography sx={{ fontSize: 12, color: D.textSec, mb: 0.4 }}><strong>Search Intent:</strong> {b.searchIntent}</Typography>}
                          {(b.headlineA || b.headlineB) && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${D.accentBorder}` }}>
                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "9.5px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Headline Variants</Typography>
                              {b.headlineA && (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.4 }}>
                                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Curiosity Hook</Typography>
                                  <Typography sx={{ fontSize: 12, color: D.textSec, flex: 1 }}>{b.headlineA}</Typography>
                                  <CopyButton text={b.headlineA} />
                                </Box>
                              )}
                              {b.headlineB && (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                  <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Direct Benefit</Typography>
                                  <Typography sx={{ fontSize: 12, color: D.textSec, flex: 1 }}>{b.headlineB}</Typography>
                                  <CopyButton text={b.headlineB} />
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Card>
                  );
                })
              : <Typography sx={{ fontSize: 13.5, color: D.textSec, mt: 2 }}>No blogs generated.</Typography>
            }
          </PlatformTabPanel>

          {/* Newsletters (index 5) */}
          <PlatformTabPanel value={tab} index={5}>
            {result.newsletters?.length > 0
              ? result.newsletters.map((n, i) => (
                  <Card key={i} sx={{ mb: 2, p: 3, position: "relative", background: "rgba(255,255,255,0.02) !important", border: `1px solid ${D.border} !important` }}>
                    <Box sx={{ position: "absolute", top: 14, right: 14 }}>
                      <CopyButton text={
                        typeof n === "object"
                          ? Object.entries(n).filter(([k, v]) => v && typeof v === "string").map(([, v]) => v).join("\n\n")
                          : String(n)
                      } />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 15, color: D.textPrimary }}>
                        {(n.subject || "").split("\n").find(l => l.trim()) || `Newsletter ${i + 1}`}
                      </Typography>
                    </Box>
                    {typeof n === "object"
                      ? (() => {
                          const LABELS = { hook: "Opening", body: "Body", reflection: "Reflection", cta: "CTA", ps: "P.S." };
                          const NL_MD = {
                            p:      ({ children }) => <Typography sx={{ fontSize: 13.5, color: "#c0c7de", lineHeight: 1.75, mb: 1 }}>{children}</Typography>,
                            strong: ({ children }) => <Box component="span" sx={{ fontWeight: 700, color: D.textPrimary }}>{children}</Box>,
                            em:     ({ children }) => <Box component="span" sx={{ fontStyle: "italic" }}>{children}</Box>,
                            ul:     ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
                            ol:     ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
                            li:     ({ children }) => <Box component="li" sx={{ mb: 0.4 }}><Typography sx={{ fontSize: 13.5, color: "#c0c7de" }}>{children}</Typography></Box>,
                            hr:     () => <Divider sx={{ my: 1.5, borderColor: D.border }} />,
                          };
                          return Object.entries(n).map(([k, v]) => {
                            if (k === "subject" || !v || typeof v !== "string") return null;
                            return (
                              <Box key={k} sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${D.border}` }}>
                                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.5, fontStyle: "normal" }}>{LABELS[k] || k}</Typography>
                                <ReactMarkdown components={NL_MD}>{v}</ReactMarkdown>
                              </Box>
                            );
                          });
                        })()
                      : <Typography sx={{ fontSize: 13.5, color: "#c0c7de", whiteSpace: "pre-wrap" }}>{String(n)}</Typography>
                    }
                    {hasContentIntelligence && result.newsletter_subject_variants?.[i] && (() => {
                      const sv = result.newsletter_subject_variants[i];
                      return (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${D.accentBorder}` }}>
                          <Typography sx={{ fontFamily: D.fontMono, fontSize: "9.5px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1 }}>Subject Line Variants</Typography>
                          {sv.direct && (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.5 }}>
                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Direct</Typography>
                              <Typography sx={{ fontSize: "12.5px", color: "#c0c7de", flex: 1 }}>{sv.direct}</Typography>
                              <CopyButton text={sv.direct} />
                            </Box>
                          )}
                          {sv.fomo && (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>FOMO</Typography>
                              <Typography sx={{ fontSize: "12.5px", color: "#c0c7de", flex: 1 }}>{sv.fomo}</Typography>
                              <CopyButton text={sv.fomo} />
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Card>
                ))
              : <Typography sx={{ fontSize: 13.5, color: D.textSec, mt: 2 }}>No newsletters generated.</Typography>
            }
          </PlatformTabPanel>

          {/* Citations (index 6) */}
          <PlatformTabPanel value={tab} index={6}>
            <CitationsPanel citations={result.citations} />
          </PlatformTabPanel>

          {/* CI tabs — Strategy, Competitor (dynamic indices starting at 7) */}
          {ALL_TABS.map((t, i) => {
            if (i < 7) return null;
            if (t.key === "calendar") return (
              <PlatformTabPanel key={t.key} value={tab} index={i}>
                <CalendarTabContent taskId={taskId} topic={topicText} />
              </PlatformTabPanel>
            );
            if (t.type === "strategy") return (
              <PlatformTabPanel key={t.key} value={tab} index={i}>
                <Box sx={{ pt: 2, pr: 5, position: "relative" }}>
                  <CopyButton text={result.strategy_brief} />
                  <ReactMarkdown components={{
                    h1: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "20px", mb: 2, color: D.textPrimary }}>{children}</Typography>,
                    h2: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "16px", mt: 3, mb: 1.5, color: D.textPrimary }}>{children}</Typography>,
                    p:  ({ children }) => <Typography sx={{ fontSize: "13.5px", lineHeight: 1.8, mb: 1.5, color: "#c0c7de" }}>{children}</Typography>,
                    li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}><Typography sx={{ fontSize: "13.5px", color: "#c0c7de" }}>{children}</Typography></Box>,
                  }}>{result.strategy_brief}</ReactMarkdown>
                </Box>
              </PlatformTabPanel>
            );
            if (t.type === "competitor") return (
              <PlatformTabPanel key={t.key} value={tab} index={i}>
                <Box sx={{ pt: 2, position: "relative" }}>
                  <CopyButton text={result.competitor_analysis} />
                  <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", whiteSpace: "pre-wrap", lineHeight: 1.75, pr: 5 }}>{result.competitor_analysis}</Typography>
                  {result.competitor_insights && (
                    <Box sx={{ mt: 3 }}>
                      <CopyButton text={result.competitor_insights} />
                      <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", whiteSpace: "pre-wrap", lineHeight: 1.75, pr: 5 }}>{result.competitor_insights}</Typography>
                    </Box>
                  )}
                </Box>
              </PlatformTabPanel>
            );
            if (t.type === "medium") return (
              <PlatformTabPanel key={t.key} value={tab} index={i}>
                {(result.medium_articles || []).map((article, ai) => {
                  const text = typeof article === "string" ? article : String(article);
                  return (
                    <Card key={ai} sx={{ mb: 3, p: 3, position: "relative", background: "rgba(255,255,255,0.02) !important", border: `1px solid ${D.border} !important` }}>
                      <Box sx={{ position: "absolute", top: 14, right: 14 }}>
                        <CopyButton text={text} />
                      </Box>
                      <ReactMarkdown components={{
                        h1: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", mb: 2, color: D.textPrimary, pr: 6 }}>{children}</Typography>,
                        h2: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", mt: 3, mb: 1.5, color: D.textPrimary }}>{children}</Typography>,
                        h3: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", mt: 2, mb: 1, color: D.textPrimary }}>{children}</Typography>,
                        p:  ({ children }) => <Typography sx={{ fontSize: "14px", lineHeight: 1.8, mb: 1.5, color: "#c0c7de" }}>{children}</Typography>,
                        strong: ({ children }) => <Box component="span" sx={{ fontWeight: 700, color: D.textPrimary }}>{children}</Box>,
                        blockquote: ({ children }) => <Box sx={{ borderLeft: `3px solid ${D.purple}`, pl: 2, py: 0.5, my: 2, background: `${D.purple}08`, borderRadius: "0 8px 8px 0" }}><Typography sx={{ fontSize: "14px", fontStyle: "italic", color: "#b8c0d8" }}>{children}</Typography></Box>,
                        ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                        li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}><Typography sx={{ fontSize: "14px", color: "#c0c7de" }}>{children}</Typography></Box>,
                        hr: () => <Divider sx={{ my: 3, borderColor: D.border }} />,
                      }}>{text}</ReactMarkdown>
                    </Card>
                  );
                })}
              </PlatformTabPanel>
            );
            return null;
          })}
        </Box>
      </Collapse>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ContentHistory() {
  const navigate = useNavigate();
  const [entries,               setEntries]               = useState([]);
  const [loading,               setLoading]               = useState(true);
  const [autoPosting,           setAutoPosting]           = useState(false);
  const [hasMedium,              setHasMedium]              = useState(false);
  const [hasContentIntelligence, setHasContentIntelligence] = useState(false);
  const [hasCompliance,          setHasCompliance]          = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) { navigate("/login", { replace: true }); return; }

    (async () => {
      try {
        const [tasksRes, meRes] = await Promise.allSettled([
          api.get("/pipeline/tasks"),
          api.get("/auth/me"),
        ]);
        const tasks = tasksRes.status === "fulfilled"
          ? (tasksRes.value.data?.tasks || []).filter(t => t.status === "complete" && t.result)
          : [];
        setEntries(tasks);
        if (meRes.status === "fulfilled") {
          setAutoPosting(!!meRes.value.data?.auto_posting);
          setHasMedium(!!meRes.value.data?.has_medium);
          setHasContentIntelligence(!!meRes.value.data?.has_content_intelligence);
          setHasCompliance(!!meRes.value.data?.has_compliance);
        }
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <style>{G}</style>

      <Box sx={{ background: D.bg, minHeight: "100vh" }}>
        {/* Nav */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 200, background: "rgba(13,14,20,0.9)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${D.border}` }}>
          <Box sx={{ maxWidth: 900, margin: "0 auto", px: 3, display: "flex", alignItems: "center", justifyContent: "space-between", height: 56 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "7px", background: `linear-gradient(135deg,${D.accent},${D.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 14, color: "white", animation: "logoGlow 3s ease infinite" }}>Z</Box>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 14.5, color: D.textPrimary }}>
                zenithpost<span style={{ color: D.accent }}>.ai</span>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <button onClick={() => navigate("/home")}
                style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`, borderRadius: 8, color: D.textSec, fontSize: 13, padding: "6px 14px", cursor: "pointer", fontFamily: D.fontBody, fontWeight: 600 }}>
                ← Back to Home
              </button>
              {autoPosting && (
                <button onClick={() => navigate("/autopost")}
                  style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)", borderRadius: 8, color: "#34d399", fontSize: 13, padding: "6px 14px", cursor: "pointer", fontFamily: D.fontBody, fontWeight: 600 }}>
                  📡 Auto Post
                </button>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 900, margin: "0 auto", px: { xs: 2, md: 3 }, py: 5 }}>
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 28, color: D.textPrimary, mb: 1 }}>
              Content History
            </Typography>
            <Typography sx={{ fontSize: 14, color: D.textSec }}>
              Your last 20 completed generations — expand any to read posts and copy content{autoPosting ? ", or open the 📅 editorial calendar to schedule everything in one click" : ""}.
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[1, 2, 3].map(i => (
                <Box key={i} className="sk" sx={{ height: 72, borderRadius: "14px" }} />
              ))}
            </Box>
          ) : entries.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10, border: `1px solid ${D.border}`, borderRadius: "16px" }}>
              <Typography sx={{ fontSize: 36, mb: 2 }}>📭</Typography>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 18, color: D.textPrimary, mb: 1 }}>No history yet</Typography>
              <Typography sx={{ fontSize: 14, color: D.textSec, mb: 3 }}>Generate your first content run from the Home page.</Typography>
              <button onClick={() => navigate("/home")}
                style={{ background: D.accent, border: "none", borderRadius: 9, color: "white", fontSize: 14, padding: "10px 24px", cursor: "pointer", fontFamily: D.fontBody, fontWeight: 700 }}>
                Go to Home
              </button>
            </Box>
          ) : (
            <Box>
              {entries.map((entry, i) => (
                <ContentEntry key={entry.task_id || i} entry={entry} autoPosting={autoPosting} hasMedium={hasMedium} hasContentIntelligence={hasContentIntelligence} hasCompliance={hasCompliance} />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "5px", background: `linear-gradient(135deg,${D.accent},${D.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: "white" }}>Z</Box>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 13, color: D.textPrimary }}>zenithpost<span style={{ color: D.accent }}>.ai</span></Typography>
            </Box>
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted }}>Content History · last 20 runs</Typography>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
